import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Trap focus inside mobile menu when open
  useEffect(() => {
    if (!menuOpen) return;
    const menu = mobileMenuRef.current;
    if (!menu) return;
    const focusable = menu.querySelectorAll<HTMLElement>(
      'a[href], button, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        hamburgerRef.current?.focus();
      }
      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    first?.focus();
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  const navLinks = [
    { label: "בית", path: "/" },
    { label: "אודות", path: "/about" },
    { label: "התפריט", path: "/menu" },
    { label: "אירועים", path: "/events" },
    { label: "גלריה", path: "/gallery" },
  ];

  return (
    <nav
      role="navigation"
      aria-label="ניווט ראשי"
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#1a0a00]/95 backdrop-blur-sm shadow-lg" : "bg-transparent"
      }`}
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          aria-label="Meet n' Meat - עמוד הבית"
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/b0584b9f674fda78ac14eb56b21cd57d/7be8eb09dc6daa3388afff0d207e476a.jpeg"
            alt="לוגו Meet n' Meat"
            className="w-20 h-20 rounded-full object-cover"
            width="80"
            height="80"
          />
          <span
            className="text-white font-bold text-xl tracking-wide"
            style={{ fontFamily: "'Assistant', sans-serif" }}
          >
            Meet n&apos; Meat
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8" role="menubar">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              role="menuitem"
              aria-current={location.pathname === link.path ? "page" : undefined}
              className={`text-base font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                location.pathname === link.path
                  ? "text-[#e85d04]"
                  : "text-white/90 hover:text-[#e85d04]"
              }`}
              style={{ fontFamily: "'Assistant', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/accessibility"
            aria-label="הצהרת נגישות"
            aria-current={location.pathname === "/accessibility" ? "page" : undefined}
            className="flex flex-col items-center justify-center bg-[#4a4a4a] hover:bg-[#5a5a5a] transition-colors duration-200 rounded-md px-3 py-1.5 cursor-pointer whitespace-nowrap min-w-[56px]"
            title="הצהרת נגישות"
          >
            <i className="ri-wheelchair-line text-white text-xl leading-none" aria-hidden="true"></i>
            <span className="text-white text-[10px] font-semibold mt-0.5" style={{ fontFamily: "'Assistant', sans-serif" }}>נגישות</span>
          </Link>
          <a
            href="#contact"
            className="bg-[#e85d04] text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-[#d44d00] transition-colors duration-200 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Assistant', sans-serif" }}
          >
            קבלו הצעת מחיר
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          ref={hamburgerRef}
          className="md:hidden text-white text-2xl cursor-pointer p-2 rounded-md"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label={menuOpen ? "סגור תפריט ניווט" : "פתח תפריט ניווט"}
        >
          <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true"></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          id="mobile-menu"
          ref={mobileMenuRef}
          className="md:hidden bg-[#1a0a00]/98 px-6 pb-6 flex flex-col gap-4"
          dir="rtl"
          role="menu"
          aria-label="תפריט ניווט נייד"
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              role="menuitem"
              aria-current={location.pathname === link.path ? "page" : undefined}
              onClick={() => setMenuOpen(false)}
              className="text-white/90 text-base font-medium py-2 border-b border-white/10 cursor-pointer"
              style={{ fontFamily: "'Assistant', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/accessibility"
            role="menuitem"
            aria-current={location.pathname === "/accessibility" ? "page" : undefined}
            onClick={() => setMenuOpen(false)}
            className="flex items-center gap-3 text-white/90 text-base font-medium py-2 border-b border-white/10 cursor-pointer"
            style={{ fontFamily: "'Assistant', sans-serif" }}
          >
            <span className="flex flex-col items-center justify-center bg-[#4a4a4a] rounded-md px-2 py-1 min-w-[44px]">
              <i className="ri-wheelchair-line text-white text-lg leading-none" aria-hidden="true"></i>
              <span className="text-white text-[9px] font-semibold mt-0.5">נגישות</span>
            </span>
            הצהרת נגישות
          </Link>
          <a
            href="#contact"
            role="menuitem"
            onClick={() => setMenuOpen(false)}
            className="bg-[#e85d04] text-white px-5 py-3 rounded-full text-sm font-semibold text-center cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Assistant', sans-serif" }}
          >
            קבלו הצעת מחיר
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
